import { UnitsEnum, UnitSystemEnum } from '@/__generated__/graphql';
import { localiseMeasurement } from '@/utils/unitSystem';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import {
  customFilterQueryVarsState,
  unitSystem as unitSystemState,
  selectedFilteredWorkpackIds,
} from '../components/Analysis/state';
import { useWorkpackFilter } from './useWorkpackFilter';

export type CustomFilterOption = {
  label?: string;
  value?: string | number;
  min?: number;
  max?: number;
  minIncluded?: boolean;
  maxIncluded?: boolean;
};

export type CustomFilterQueryVars = {
  [key: string]: (
    | {
        label?: string;
        value?: string | number;
        min?: number;
        max?: number;
        minIncluded?: boolean;
        maxIncluded?: boolean;
      }
    | string
  )[];
};

export type CustomFilterType = 'range' | 'absolute';

export type CustomFilterConfig = {
  key: string;
  label: string;
  filterType: CustomFilterType;
  options: CustomFilterOption[];
  sourceUnit?: UnitsEnum;
};
type UseCustomFiltersResult = {
  customFiltersConfig: CustomFilterConfig[];
  customFilterValues: Record<string, string[]>;
  handleCustomFilterChange: (key: string, values: string[]) => void;
  customFilterQueryVars: CustomFilterQueryVars;
  resetCustomFilters: () => void;
};

const deckRelativeHieghtRanges = [
  { min: 0, max: 1, minIncluded: true, maxIncluded: true },
  { min: 1, max: 2, minIncluded: true, maxIncluded: true },
  { min: 2, max: 3, minIncluded: true, maxIncluded: true },
  { min: 3, max: undefined, minIncluded: false, maxIncluded: false },
];

export function useCustomFilters(): UseCustomFiltersResult {
  const unitSystem = useAtomValue(unitSystemState);
  const [localCustomFilterValues, setLocalCustomFilterValues] = useState<Record<string, string[]>>(
    {}
  );
  const [workpackIds, setWorkpackIds] = useAtom(selectedFilteredWorkpackIds);

  // Merge local filter values with global workpackIds
  const customFilterValues = useMemo(
    (): Record<string, string[]> => ({
      ...localCustomFilterValues,
      ...(workpackIds.length > 0 ? { workpackIds } : {}),
    }),
    [localCustomFilterValues, workpackIds]
  );
  const setCustomFilterQueryVarState = useSetAtom(customFilterQueryVarsState);
  const { workpackOptions } = useWorkpackFilter();

  const resetCustomFilters = useCallback(() => {
    setLocalCustomFilterValues({});
    setWorkpackIds([]);
  }, [setWorkpackIds]);

  const unitLabel = unitSystem === UnitSystemEnum.Imperial ? 'ft' : 'm';

  const getNumericValue = (
    value: number | undefined,
    unitSystem: UnitSystemEnum,
    sourceUnit: UnitsEnum,
    showUnitLabel: boolean
  ) => {
    const result = Math.round(
      Number(
        localiseMeasurement({
          unitSystem,
          value,
          sourceUnit,
          showUnitLabel,
        })
      )
    );
    return result;
  };

  const getLabelFromMinAndMaxValues = (
    min: number | undefined,
    max: number | undefined,
    minValue: number | undefined,
    maxValue: number | undefined,
    unitLabel: string
  ) => {
    const label =
      min === undefined
        ? `<${maxValue} ${unitLabel}`
        : max === undefined
          ? `>${minValue} ${unitLabel}`
          : `${minValue}-${maxValue} ${unitLabel}`;
    return label;
  };

  const getValueFromMinAndMaxValues = (min: number | undefined, max: number | undefined) => {
    return `${min}-${max}`;
  };

  const customFiltersConfig = useMemo((): CustomFilterConfig[] => {
    const filters: CustomFilterConfig[] = [
      {
        key: 'deckRelativeHeight',
        label: 'Height from deck',
        filterType: 'range',
        sourceUnit: UnitsEnum.Meter,
        options: deckRelativeHieghtRanges.map(({ min, max, minIncluded, maxIncluded }) => {
          const minValue = getNumericValue(min, unitSystem, UnitsEnum.Meter, false);
          const maxValue = getNumericValue(max, unitSystem, UnitsEnum.Meter, false);
          const label = getLabelFromMinAndMaxValues(min, max, minValue, maxValue, unitLabel);
          const value = getValueFromMinAndMaxValues(minValue, maxValue);
          return {
            label,
            value,
            min,
            max,
            minIncluded,
            maxIncluded,
          };
        }),
      },
    ];

    // Add workpack filter if workpacks are available
    if (workpackOptions.length > 0) {
      filters.push({
        key: 'workpackIds',
        label: 'Work Pack',
        filterType: 'absolute',
        options: workpackOptions.map((option) => ({
          label: option.label,
          value: option.value,
        })),
      });
    }

    return filters;
  }, [workpackOptions, unitSystem, unitLabel]);

  const customFilterQueryVars = useMemo(() => {
    const vars: CustomFilterQueryVars = {};

    customFiltersConfig.forEach((filter) => {
      const selected = customFilterValues[filter.key];

      if (!selected || selected.length === 0) {
        return;
      }

      if (filter.filterType === 'range') {
        vars[filter.key] = filter.options
          .filter((opt) => selected.includes(String(opt.value)))
          .map((opt) => ({
            min: opt.min,
            max: opt.max,
            minIncluded: opt.minIncluded,
            maxIncluded: opt.maxIncluded,
          }));
      } else {
        // For workpackIds, we just need the value, not the full object
        if (filter.key === 'workpackIds') {
          vars[filter.key] = selected;
        } else {
          vars[filter.key] = selected.map((val) => ({
            label: val,
            value: val,
          }));
        }
      }
    });
    setCustomFilterQueryVarState(vars);
    return vars;
  }, [customFiltersConfig, setCustomFilterQueryVarState, customFilterValues]);

  const handleCustomFilterChange = useCallback(
    (key: string, values: string[]) => {
      if (key === 'workpackIds') {
        setWorkpackIds(values);
      } else {
        setLocalCustomFilterValues((prev: Record<string, string[]>) => ({
          ...prev,
          [key]: values,
        }));
      }
    },
    [setWorkpackIds]
  );
  return {
    customFiltersConfig,
    customFilterValues,
    handleCustomFilterChange,
    customFilterQueryVars,
    resetCustomFilters,
  };
}
